import { Icon, Text, useColors } from "@certn/cascadia-react";
import { CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { ReactNode } from "react";

import { useCheckFlowNavbarStyles } from "./styles";

type ListItemProps = {
  onPress: () => Promise<void> | void;
  isActive: boolean;
  icon: CascadiaStandardIcon;
  children: ReactNode;
  isDisabled: boolean;
  isCompleted?: boolean;
  hasUnsavedValues?: boolean;
  testID?: string;
  sectionKey?: string;
};

export function SectionsListItem({
  onPress,
  isActive,
  children,
  icon,
  isDisabled,
  isCompleted,
  hasUnsavedValues,
  testID = "section-list-item",
  sectionKey,
}: ListItemProps) {
  const styles = useCheckFlowNavbarStyles();
  const colors = useColors();
  const iconType = isDisabled
    ? "lock"
    : isCompleted
      ? "check_circle"
      : hasUnsavedValues
        ? "warning"
        : icon;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
    if (isDisabled) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      if (onPress) {
        event?.preventDefault();
        onPress();
      }
    }
  };

  const handlePress = () => {
    if (isDisabled) {
      return;
    }
    onPress();
  };

  return (
    <li
      role="menuitem"
      className={css(
        styles.listItem,
        isActive &&
          css({
            background: colors["background-accent-neutral-weak"],
          }),
        !isDisabled &&
          css({
            "&:focus, &:hover": {
              cursor: "pointer",
              background: colors["background-accent-neutral-medium"],
            },
          })
      )}
      onClick={handlePress}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      data-testid={`${testID}-${sectionKey}-${slugify(iconType)}`}
    >
      <Icon
        name={iconType}
        padding="space-2"
        color={
          isDisabled
            ? "icon-disabled"
            : isCompleted
              ? "icon-success"
              : hasUnsavedValues
                ? "icon-warning"
                : "icon-base"
        }
        fill={isCompleted}
      />

      <Text as="h4" color={isDisabled ? "text-disabled" : "text-base"}>
        {children}
      </Text>
    </li>
  );
}
